import { FunctionComponent, useState } from 'react'
import Title from '@/components/_custom/Title/title'
import { Daycare, DaycareType } from '@/types/Daycare'
import styles from './parentAutolink.module.scss'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { Autorenew, ContentCopy, Email, PowerSettingsNew, QrCodeScanner } from '@mui/icons-material'
import { useAuth } from '@/modules/context/AuthContext'
import UserInput from '@/components/_custom/UserInput/userInput'
import ConfirmRenewDialog from '../Dialog/confirmRenewDialog'
import { ACTIVE_AUTO_LINK, DEACTIVE_AUTO_LINK, GET_AUTO_LINK_MAIL } from '@/modules/API/software/autoLinkHandler'
import ShowQRCodeDialog from '../../../../families/list/header/Dialog/showQRCodeDialog.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ParentAutolinkProps {
    daycare: Daycare
}

const ParentAutolink: FunctionComponent<ParentAutolinkProps> = ({ daycare }: ParentAutolinkProps) => {
    const { t } = useTranslation()

    const { user, fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const autoLinkPrefix: string = (import.meta.env.VITE_PUBLIC_URL || 'https://app.cald.fr') + '/?autoLink='

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const [autoLinkSuffix, setAutoLinkSuffix] = useState<string>(isRpeOrLaep ? '&role=assmat' : '&role=parent')

    const [inviteParentRPE, setInviteParentRPE] = useState<boolean>(false)

    const [autoLink, setAutoLink] = useState<string>(autoLinkPrefix + daycare.autoLink + autoLinkSuffix)
    const [autoLinkActive, setAutoLinkActive] = useState<boolean>(daycare.autoLinkEnable || false)

    const [mailSent, setMailSent] = useState<boolean>(false)

    const [isRenewDialogOpen, setRenewDialogOpen] = useState<boolean>(false)

    const [isQRCodeDialogOpen, setQRCodeDialogOpen] = useState<boolean>(false)

    function renewAutoLink(newAutoLink: string): void {
        fetchCurrentDaycare()
        setAutoLink(autoLinkPrefix + newAutoLink)
    }

    const [sendAutoLinkMail] = useMutation(GET_AUTO_LINK_MAIL, {
        variables: {
            daycareId: daycare.id,
            inviteParent: isRpeOrLaep ? inviteParentRPE : true
        },
        onCompleted: () => {
            setMailSent(true)

            showNotificationSuccess(t('autoLinkMailSuccess'))
        },
        onError: () => {
            showNotificationError(t('autoLinkMailError'))
        }
    })

    const [deactiveAutoLink] = useMutation(DEACTIVE_AUTO_LINK, {
        variables: {
            daycareId: daycare.id
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            setAutoLinkActive(false)

            showNotificationSuccess(t('autoLinkDeactivated'))
        },
        onError: () => {
            showNotificationError(t('autoLinkDeactivatedError'))
        }
    })

    const [activeAutoLink] = useMutation(ACTIVE_AUTO_LINK, {
        variables: {
            daycareId: daycare.id
        },
        onCompleted: () => {
            fetchCurrentDaycare()
            setAutoLinkActive(true)

            showNotificationSuccess(t('autoLinkActivated'))
        },
        onError: () => {
            showNotificationError(t('autoLinkActivatedError'))
        }
    })

    function copyLink(): void {
        navigator.clipboard.writeText(autoLink).then(() => {
            showNotificationSuccess(t('autoLinkCopied'))
        })
    }

    function handleChangeCheckbox(checked: boolean): void {
        const suffix: string = checked ? '&role=parent' : '&role=assmat'

        setInviteParentRPE(checked)
        setAutoLinkSuffix(suffix)
        setAutoLink(autoLinkPrefix + daycare.autoLink + suffix)
    }

    return (
        <div className={styles.AutoLinkPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={isRpeOrLaep ? t('publicAutoLink') : t('parentAutoLink')} className={styles.Title} />
                    {user?.currentDaycareRights.canWriteParamsDaycare && autoLinkActive && (
                        <ButtonTextIcon
                            type={'custom'}
                            className={styles.Button}
                            onClick={deactiveAutoLink}
                            customText={t('deactive')}
                            customIcon={<PowerSettingsNew />}
                        />
                    )}
                </div>
                <div className={styles.Container}>
                    {!autoLinkActive && (
                        <div className={styles.InputContainerDesactive}>
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                onClick={activeAutoLink}
                                customText={t('active')}
                                customIcon={<PowerSettingsNew />}
                            />
                        </div>
                    )}
                    {isRpeOrLaep && (
                        <div className={styles.CheckBoxContainer}>
                            <Checkbox
                                text={t('inviteFamily')}
                                className={styles.Checkbox}
                                checked={inviteParentRPE}
                                onChange={handleChangeCheckbox}
                                disabled={!isRpeOrLaep}
                            />
                        </div>
                    )}
                    <div className={styles.InputContainer}>
                        <UserInput
                            label={t('autoLink')}
                            value={autoLink}
                            readOnly={autoLinkActive}
                            disabled={!autoLinkActive}
                            fullWidth
                        />
                        {autoLinkActive && <ContentCopy className={styles.Icon} onClick={copyLink} />}
                    </div>
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={sendAutoLinkMail}
                            customText={t('receiveMail')}
                            disabled={!autoLinkActive || mailSent}
                            customIcon={<Email />}
                            className={styles.Button}
                        />
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={() => setQRCodeDialogOpen(true)}
                            customText={t('showQRCode')}
                            disabled={!autoLinkActive}
                            customIcon={<QrCodeScanner />}
                            className={styles.Button}
                        />
                        {user?.currentDaycareRights.canWriteParamsDaycare && (
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                onClick={() => setRenewDialogOpen(true)}
                                customText={t('renew')}
                                customIcon={<Autorenew />}
                                disabled={!autoLinkActive}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ShowQRCodeDialog
                open={isQRCodeDialogOpen}
                onClose={() => setQRCodeDialogOpen(false)}
                autoLink={autoLink}
                pdfVariant={isRpeOrLaep ? 'rpe' : 'eaje'}
                daycareName={daycare.name}
                daycareLogoBase64Data={daycare.logoBase64Data}
            />
            <ConfirmRenewDialog
                open={isRenewDialogOpen}
                staffLink={false}
                closeDialog={() => setRenewDialogOpen(false)}
                daycareId={daycare.id}
                renewAutoLink={renewAutoLink}
            />
        </div>
    )
}

export default ParentAutolink
